import { PositionSetting } from "domain/positionSetting";
import PositionOrder from "./positionOrder";
import * as NP from 'number-precision';

export class PositionBalance {
    id: number = 0;
    positionSetting: PositionSetting = null;
    physicalQuantity: number = 0;
    hedgeQuantity: number = 0;
    previousDayCarryOver: number = 0;
    workingOrders: PositionOrder[] = [];
    netQuantity: number = 0;
    netTransfer: number = 0;
};

export interface ThresholdCheck {
    exceedsThreshold: boolean;
    exceedsOppositeThreshold: boolean;
}

function getWorkingOrderQuantity(order: PositionOrder | null | undefined): number {
    if (order == null || order.isSpread) return 0;
    const quantity = order.quantity ?? 0;
    return order.customerWillSell ? -quantity : quantity;
}

export function sumWorkingOrderQuantity(orders: PositionOrder[] | null | undefined): number {
    if (!orders || orders.length === 0) return 0;
    return orders.reduce((total, order) => NP.plus(total, getWorkingOrderQuantity(order)), 0);
}

export function sumWorkingOrderLots(orders: PositionOrder[] | null | undefined): number {
    if (orders == null) return 0;
    return orders.reduce((acc: number, cur: PositionOrder) => acc + cur.numberOfContracts - cur.filledContracts, 0);
}

export function getPositionBalance(balance: PositionBalance | null | undefined): number {
    if (balance == null) return 0;
    return NP.plus(balance.netQuantity ?? 0, sumWorkingOrderQuantity(balance.workingOrders));
}

export function checkPositionBalanceThresholds(balance: PositionBalance): ThresholdCheck {
    const result: ThresholdCheck = { exceedsThreshold: false, exceedsOppositeThreshold: false };

    if (!balance) return result;

    const { positionSetting: setting, netQuantity } = balance;
    const {
        commodity,
        targetBalance,
        autoOrderWillSell,
        miniContractMinimum,
        fullContractMinimum
    } = setting;

    if (!commodity) return result;

    let contractMinQty: number | null = null;

    if (miniContractMinimum != null && commodity.miniContractSize) {
        // mini % x mini contract size
        contractMinQty = NP.times(NP.divide(miniContractMinimum, 100), commodity.miniContractSize);
    } else if (fullContractMinimum != null && commodity.contractSize) {
        // full % x full contract size
        contractMinQty = NP.times(NP.divide(fullContractMinimum, 100), commodity.contractSize);
    }

    if (contractMinQty == null) return result;

    const highThreshold = NP.plus(targetBalance, contractMinQty);
    const lowThreshold = NP.minus(targetBalance, contractMinQty);

    if (
        (autoOrderWillSell && netQuantity >= highThreshold) ||
        (!autoOrderWillSell && netQuantity <= lowThreshold)
    ) {
        result.exceedsThreshold = true;
    }

    if (
        (autoOrderWillSell && netQuantity <= lowThreshold) ||
        (!autoOrderWillSell && netQuantity >= highThreshold)
    ) {
        result.exceedsOppositeThreshold = true;
    }

    return result;
}

export function isAutoPositionEnabledForBalance(balance: PositionBalance): boolean {
    return !balance.positionSetting.isAutoPositionDisabled && !balance.positionSetting.commodity.isAutoPositionDisabled;
}